import { UserLogListMapping } from '@app-user-management/constants/user-activity-log.constants';
import { CookieConsent, LoginUserMetadata, QuickTips, UserTag } from '@app-core/models/core.model';

export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
}

export interface UserFleetInfo {
  fleetId: string;
  role: {
    level: number;
    roleId: number;
    roleName: string;
  };
  userTags?: UserTag[];
}

export interface User {
  email: string;
  name: string;
  fleets: UserFleetInfo[];
  status: string;
  userId: string;
  metadata: any;
  portalId: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface Metadata {
  userMetadata?: LoginUserMetadata;
}

export interface CreatedUser {
  id: number;
  name: string;
  email: string;
  roleId: string;
  status: string;
  userId: string;
  fleetId: string;
  metadata: Metadata;
  portalId: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Request body classes
export class ListUsersParams {
  public limit?: number;
  public offset?: number;
  public status?: string;
  public search?: string;
  public ['userTags[]']?: Array<number>;

  constructor(params: { limit?: number; offset?: number; status?: UserStatus; search?: string; userTags?: Array<number> }) {
    if (params.status) {
      this.status = params.status;
    } else {
      delete this.status;
    }

    if (params.limit) {
      this.limit = params.limit;
    } else {
      delete this.limit;
    }

    if (params.offset !== undefined && params.offset !== null) {
      this.offset = params.offset;
    } else {
      delete this.offset;
    }

    if (params.search) {
      this.search = params.search;
    } else {
      delete this.search;
    }

    if (Array.isArray(params.userTags)) {
      this['userTags[]'] = params.userTags;
    } else if (params.userTags !== undefined && params.userTags !== null) {
      this['userTags[]'] = [params.userTags];
    }
  }
}

export class CreateUserReqBody {
  public email: string;
  public name: string;
  public roleId: number;
  public tagAccessRule?: any;

  constructor(formData: any, tagAccessRule: any) {
    this.email = formData.email;
    this.name = formData.name;
    this.roleId = formData.roleId;
    if (tagAccessRule && tagAccessRule.groups?.length) {
      this.tagAccessRule = tagAccessRule;
    }
  }
}

export class EditUserReqBody extends CreateUserReqBody {
  constructor(formData: any, tagAccessRule: any) {
    super(formData, tagAccessRule);
    delete this.email;
  }
}

export class UpdateUserStatusReqBody {
  public status: UserStatus;

  constructor(status: UserStatus) {
    this.status = status;
  }
}

export interface UpdateUserMetadataPayload {
  dateFormat?: string;
  isReadableDateFormat?: boolean;
  metricUnit?: string;
  timezone?: string;
  language?: string;
  theme?: string;
  preferredFleet?: string;
  tablePageSize?: string;
  mfaEnabled?: boolean;
  cookieConsent?: CookieConsent;
  incidentsTablePageSize?: number;
  pinnedReports?: string[];
  quickTips?: QuickTips[];
}

export class UpdateUserMetadataReqBody {
  userMetadata: UpdateUserMetadataPayload;

  constructor(data: UpdateUserMetadataPayload) {
    this.userMetadata = {
      dateFormat: data.dateFormat,
      isReadableDateFormat: data.isReadableDateFormat,
      metricUnit: data.metricUnit,
      timezone: data.timezone,
      language: data.language,
      theme: data.theme,
      preferredFleet: data.preferredFleet,
      tablePageSize: data.tablePageSize,
      mfaEnabled: data.mfaEnabled,
      cookieConsent: data.cookieConsent,
      incidentsTablePageSize: data.incidentsTablePageSize,
      pinnedReports: data.pinnedReports,
      quickTips: data.quickTips,
    };
  }
}

// Responses
export interface ListUsersResp {
  code: number;
  rows: User[];
  skip: number;
  limit: number;
  totalRows: number;
}

export interface UserDetailsResp {
  // code: number;
  data: User;
}

export interface CreateUserResp {
  code: number;
  user: CreatedUser;
  message: string;
}

export interface UpdateUserResp extends CreateUserResp {}

export interface ssoUserDetails {
  userMetadata: {
    name: string;
    email: string;
  };
  userPreferences: userPreferences;
  consentTimestamp: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  fleets: Omit<UserFleetInfo, 'role'>[];
}

export interface CreateSSOUserDetails {
  payload: {
    userPreferences: userPreferences;
    consentTimestamp?: string;
  };
}

export interface updateSSOUserConsent {
  payload: {
    userPreferences?: userPreferences;
    consentTimestamp?: string;
  };
}

export interface userPreferences {
  metricUnit?: string;
  dateFormat?: string;
  language?: string;
  theme?: string;
  timezone?: string;
  quickTips?: any[];
}

interface UpdateSSOUserDetailsPayload {
  userPreferences: userPreferences;
  consentTimestamp?: string;
}

export class UpdateSSOUserDetailsFromHeader {
  public payload: UpdateSSOUserDetailsPayload;

  constructor(data: userPreferences, consentTimestamp?: string) {
    this.payload = {
      userPreferences: {
        dateFormat: data.dateFormat,
        metricUnit: data.metricUnit,
        timezone: data.timezone,
        language: data.language,
        theme: data.theme,
      },
      consentTimestamp: consentTimestamp,
    };
  }
}

export type UserListMappingKey = (typeof UserLogListMapping)[keyof typeof UserLogListMapping]['key'];

export class UsersLogListParams {
  public limit?: number;
  public offset?: number;
  public startDate?: string;
  public endDate?: string;
  public userId?: string;

  constructor(params: { limit?: number; offset?: number; startDate?: string; endDate?: string; userId?: string }) {
    if (params.limit !== undefined) {
      this.limit = params.limit;
    } else {
      delete this.limit;
    }

    if (params.offset !== undefined && params.offset !== null) {
      this.offset = params.offset;
    } else {
      delete this.offset;
    }

    if (params.startDate) {
      this.startDate = params.startDate;
    } else {
      delete this.startDate;
    }

    if (params.endDate) {
      this.endDate = params.endDate;
    } else {
      delete this.endDate;
    }

    if (params.userId) {
      this.userId = params.userId;
    } else {
      delete this.userId;
    }
  }
}

export class UsersLogDetailParams {
  public limit?: number;
  public offset?: number;
  public logId?: string;

  constructor(params: { limit?: number; offset?: number; logId?: string }) {
    if (params.limit !== undefined) {
      this.limit = params.limit;
    } else {
      delete this.limit;
    }

    if (params.offset !== undefined && params.offset !== null) {
      this.offset = params.offset;
    } else {
      delete this.offset;
    }

    if (params.logId) {
      this.logId = params.logId;
    } else {
      delete this.logId;
    }
  }
}

export interface UserLogListResponse {
  rows: UserLog[];
  limit: number;
  skip: number;
  totalCount: number;
}

export interface UserLog {
  logId: string;

  activityType: string | null;
  ingestedTimestamp: string | null;
  timestampUTC: string | null;
  timestamp: string | null;
  fields?: Field[];
  user: User;
}

export interface Field {}

export interface User {
  loginType: string | null;
  name: string | null;
}

export type ActivityType =
  | 'FleetSdkBasicConfigurationsChange'
  | 'FleetSdkAdvancedConfigurationsChange'
  | 'FleetSdkBasicConfigurationsDefault'
  | 'FleetDriverConsentForAudioChange'
  | 'FleetCoachingConfigurationsChange'
  | 'FleetAttributeAdded'
  | 'FleetAttributeChanged'
  | 'FleetAttributeDeleted'
  | 'FleetTagsAdded'
  | 'FleetTagChanged'
  | 'FleetTagDeleted'
  | 'FleetEntityChanged'
  | 'FleetSdkCustomEventConfigurationsChange';

export interface UserLogDetailsResponse {
  activityType: ActivityType;
  timestampUTC: string;
  timestamp: string;
  clientId: string;
  fleetId: string;
  userMetadata: UserMetadata;
  activityMetadata?: ActivityMetadata;
  fields: FieldChange[];
  totalCount: number;
}

export interface UserMetadata {
  email: string;
  userId: string;
  userHash: string;
  name: string;
  userType: string;
  loginType: string;
  browserTimezoneId: string;
  applicationTimezoneId?: string;
  ipAddress?: string;
  userAgent?: string;
}

export interface FieldChange {
  field: string;
  subField?: string;
  oldValue?: any;
  newValue: any;
  title?: string;
  activityMetadata?: Object;
}

export interface ActivityMetadata {
  [key: string]: any;
}
